import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Send } from "lucide-react";
import { NAV_LINKS, X_URL, TELEGRAM_URL } from "@/config";
import { scrollToId } from "@/lib/scroll";
import { XIcon } from "./OfficialLinks";
import { CowMark } from "./CowMark";
import { LanguagePicker } from "./LanguagePicker";
import { useLanguage } from "@/i18n/LanguageContext";

const NAV_KEY = {
  top: "nav.home",
  about: "nav.about",
  tokenomics: "nav.tokenomics",
  "how-to-buy": "nav.howToBuy",
  roadmap: "nav.roadmap",
  community: "nav.community",
  "official-links": "nav.officialLinks",
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-purple-500/20 bg-[#08080F]/70 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="flex items-center gap-2.5"
          aria-label="SOLMOO home"
        >
          <CowMark size={34} />
          <span className="font-display text-lg font-bold tracking-wide text-[#F5F3FF]">
            SOLMOO
          </span>
        </button>

        <div className="hidden items-center gap-5 lg:flex xl:gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-gray-300 transition-colors hover:text-[#14F195]"
            >
              {t(NAV_KEY[l.id])}
            </button>
          ))}
          <LanguagePicker />
          <a
            data-testid="nav-x-link"
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="SOLMOO on X"
            className="text-gray-400 transition-colors hover:text-[#14F195]"
          >
            <XIcon size={16} />
          </a>
          <a
            data-testid="nav-telegram-link"
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="SOLMOO on Telegram"
            className="text-gray-400 transition-colors hover:text-[#14F195]"
          >
            <Send size={16} />
          </a>
          <button
            data-testid="nav-cta-buy"
            onClick={() => go("official-links")}
            className="bg-solana-gradient rounded-full px-5 py-2 text-sm font-bold text-white shadow-[0_4px_20px_rgba(153,69,255,0.3)] transition-transform hover:scale-105"
          >
            {t("nav.buy")}
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="rounded-lg p-2 text-gray-200 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-purple-500/10 bg-[#08080F]/95 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              <LanguagePicker mobile />
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-gray-200 hover:bg-[#14141E]"
                >
                  {t(NAV_KEY[l.id])}
                </button>
              ))}
              <div className="mt-2 flex items-center gap-2 px-1">
                <a
                  data-testid="mobile-nav-x-link"
                  href={X_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-purple-500/30 py-2.5 text-sm font-semibold text-gray-200"
                >
                  <XIcon size={15} /> X
                </a>
                <a
                  data-testid="mobile-nav-telegram-link"
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-purple-500/30 py-2.5 text-sm font-semibold text-gray-200"
                >
                  <Send size={15} /> Telegram
                </a>
              </div>
              <button
                data-testid="mobile-nav-cta-buy"
                onClick={() => go("official-links")}
                className="bg-solana-gradient mt-2 rounded-full px-5 py-3 text-center text-base font-bold text-white"
              >
                {t("nav.buy")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
